import { ElementRef, Injectable, QueryList } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { selectNotifications } from '../notification/notification.selectors';
import { NotificationState } from '../notification/notification.state';
import { AppState } from 'src/app/app.state';

@Injectable({
	providedIn: 'root',
})
export class NotificationFocusService {
	/**
	 * The Element we want to return focus to after a modal is closed
	 */
	private _focusedElement: HTMLElement;
	/**
	 * how many remaining notifications exist after the current one
	 */
	private _remainingNotifications: number;
	/**
	 * if the service should be paused or not
	 */
	private _blockService: boolean = false;
	/**
	 * @param _store$ Global Store
	 */
	constructor(_store$: Store<AppState>) {
		_store$.pipe(select(selectNotifications)).subscribe((state: NotificationState) => {
			this._remainingNotifications = state?.notifications?.length;
		});
	}

	/**
	 * sets the element that we should return focus to after a modal is closed
	 * @param element can be an HTML Element, QueryList, or contrain a nativeElement.
	 */
	public setFocusedElement(
		element: HTMLElement | HTMLElement[] | QueryList<any> | QueryList<any>[] | ElementRef | ElementRef[]
	) {
		if (element instanceof QueryList) {
			this._focusedElement = element.get(0)?.nativeElement;
		} else if (
			element instanceof ElementRef &&
			element?.nativeElement &&
			element?.nativeElement instanceof HTMLElement
		) {
			this._focusedElement = element.nativeElement;
		} else if (element instanceof HTMLElement) {
			this._focusedElement = element;
		}
	}

	/**
	 * if an element is set, the service is not paused, and there are no remaining notifications, sets focus
	 */
	public returnFocus(): void {
		document.addEventListener('keypress', this._pauseEnter);
		if (this._focusedElement && this._remainingNotifications === 0 && !this._blockService) {
			this._focusedElement.focus();
		}
		setTimeout(() => {
			document.removeEventListener('keypress', this._pauseEnter);
		}, 0);
	}

	/**
	 * Prevents the Enter key from being pressed
	 */
	private _pauseEnter(e: KeyboardEvent): void {
		if (e.key == 'Enter' || e.keyCode == 13) {
			e.preventDefault();
		}
	}

	/**
	 * Can be called to pause the service
	 * @param block true to stop the service, false to resume
	 */
	public setBlockService(block: boolean): void {
		if (block === true) {
			this._blockService = true;
		} else {
			setTimeout(() => {
				this._blockService = false;
			}, 30);
		}
	}
}
